/**
 * The tool wheel's swap menu: opened on a tile while the wheel is in edit
 * mode, it lists the drawing catalog by group, then the drawing bar's
 * controls (magnet, lock, hide, remove all), and puts the choice into that
 * slot. A choice already in the wheel trades places with the slot's own.
 *
 * Same shell as the chart's other right-click menus (`PriceScaleContextMenu`):
 * portaled, clamped to the viewport, closed on Escape or an outside click,
 * rows from `contextMenuChrome`.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { usePortalDismiss } from '../hooks/usePortalDismiss'
import { OVERLAY_GROUPS } from '../utils/overlays'
import { DEFAULT_TOOL_WHEEL_SLOTS } from '../utils/toolWheelGeometry'
import { MenuRow, Submenu } from './contextMenuChrome'
import { ControlIcons, GroupIcons } from './drawingBarChrome'
import { ToolIcons } from './toolIcons'
import { WHEEL_CONTROLS, wheelSlotLabel } from './toolWheelControls'
import { Z_MODAL_CHROME } from './zLayers'

export interface ToolWheelSwapMenuProps {
  /** Viewport coordinates of the click that opened the menu. */
  x: number
  y: number
  slot: number
  slots: readonly string[]
  onPick: (overlayName: string) => void
  onClose: () => void
}

function MenuIcon({ children }: { children: ReactNode }) {
  return <span className="flex [&>svg]:h-3.5 [&>svg]:w-3.5">{children}</span>
}

/** Dividers on a popover surface use the strong border token. */
function MenuDivider() {
  return <div className="my-1 h-px bg-border-strong" role="separator" />
}

function InWheel() {
  return (
    <>
      {' '}
      <span className="ml-1 text-[hsl(var(--chart-text-muted))]">in wheel</span>
    </>
  )
}

export function ToolWheelSwapMenu({ x, y, slot, slots, onPick, onClose }: ToolWheelSwapMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)

  // Render at the click, then clamp once measured. Hidden until measured.
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)
  useLayoutEffect(() => {
    const el = menuRef.current
    if (!el) return
    const margin = 8
    const left = Math.max(margin, Math.min(x, window.innerWidth - el.offsetWidth - margin))
    const top = Math.max(margin, Math.min(y, window.innerHeight - el.offsetHeight - margin))
    setPos({ left, top })
  }, [x, y])

  usePortalDismiss(menuRef, onClose)

  const current = slots[slot]
  const fallback = DEFAULT_TOOL_WHEEL_SLOTS[slot]
  const currentLabel = wheelSlotLabel(current)
  const elsewhere = (name: string) => name !== current && slots.includes(name)

  const menu = (
    <div
      ref={menuRef}
      role="menu"
      aria-label={`Swap ${currentLabel}`}
      data-chart-popup=""
      className="fixed flex min-w-[200px] flex-col rounded-lg border border-primary/15 bg-popover py-1 text-[hsl(var(--chart-text))] shadow-2xl shadow-black/50"
      style={{
        zIndex: Z_MODAL_CHROME,
        left: pos?.left ?? x,
        top: pos?.top ?? y,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      <div className="px-2.5 py-1 text-xs text-[hsl(var(--chart-text-muted))]">
        Swap <span className="font-semibold text-[hsl(var(--chart-text))]">{currentLabel}</span>
      </div>
      <MenuDivider />
      {OVERLAY_GROUPS.map((group) => (
        <Submenu
          key={group.label}
          label={group.label}
          icon={<MenuIcon>{GroupIcons[group.label]}</MenuIcon>}
        >
          {group.tools.map((tool) => (
            <MenuRow
              key={tool.name}
              glyph={null}
              icon={<MenuIcon>{ToolIcons[tool.name]}</MenuIcon>}
              trailingGlyph={tool.name === current ? 'check' : null}
              onSelect={() => onPick(tool.name)}
            >
              {tool.label}
              {elsewhere(tool.name) && <InWheel />}
            </MenuRow>
          ))}
        </Submenu>
      ))}
      <MenuDivider />
      <Submenu label="Controls" icon={<MenuIcon>{ControlIcons.magnetOff}</MenuIcon>}>
        {WHEEL_CONTROLS.map((control) => (
          <MenuRow
            key={control.id}
            glyph={null}
            icon={<MenuIcon>{ControlIcons[control.icon(false)]}</MenuIcon>}
            trailingGlyph={control.id === current ? 'check' : null}
            onSelect={() => onPick(control.id)}
          >
            {control.label}
            {elsewhere(control.id) && <InWheel />}
          </MenuRow>
        ))}
      </Submenu>
      {fallback && fallback !== current && (
        <>
          <MenuDivider />
          <MenuRow glyph={null} onSelect={() => onPick(fallback)}>
            {`Restore default (${wheelSlotLabel(fallback)})`}
          </MenuRow>
        </>
      )}
    </div>
  )

  return createPortal(menu, document.body)
}
